import React from 'react';
import { VStack, HStack, Text, Badge, Button, Link, Icon } from '@chakra-ui/react';
import { User, Building2, Edit, UserCheck } from 'lucide-react';
import Drawer, { DrawerHeader, DrawerTitle, DrawerBody, DrawerFooter, DrawerCloseTrigger } from '@/components/ui/Drawer';
import DetailsSection from '@/components/ui/DetailsSection';
import withHashDrawer from '@/components/ui/withHashDrawer';
import { BaseDetailsModalProps } from '@/types/DetailsModalTypes';
import { getUserStatusColor } from '../api';
import { getRoleLabelByValue } from '../constants';
import { useGetUserByIdQuery } from '../api';
import LoadingSkeleton from '@/components/LoadingSkeleton';
import ErrorAlert from '@/components/ErrorAlert';
import RelativeTime from '@/components/ui/RelativeTime';
import { getUserProfileUrl } from '../../../config/env';
import { ClarityChatbotOptInSection } from '@/entities/clarity/ClarityChatbotOptInSection';
import { ProfileAccountStatusSection } from './ProfileAccountStatusSection';
import { isFlagOn } from '../flags';

interface UserDetailsModalProps extends BaseDetailsModalProps {
  onImpersonateUser?: (userId: number) => void;
  isImpersonatingUser?: boolean;
}

const UserDetailsModal: React.FC<UserDetailsModalProps> = ({
  isOpen,
  onClose,
  entityId,
  navigateToModal,
  onImpersonateUser,
  isImpersonatingUser = false,
}) => {
  const { data: user, error, isLoading } = useGetUserByIdQuery(entityId!, {
    skip: !entityId,
  });

  const renderContent = () => {
    if (isLoading) {
      return <LoadingSkeleton />;
    }

    if (error) {
      return <ErrorAlert isError={true} />;
    }

    if (!user) {
      return <Text>User not found</Text>;
    }

    return (
      <VStack gap={6} align="stretch">
        {/* Basic Information Section */}
        <DetailsSection
          title="Basic Information"
          icon={User}
          items={[
            {
              label: "ID",
              value: user.id
            },
            {
              label: "Email", 
              value: user.email
            },
            {
              label: "Full Name",
              value: `${user.first_name} ${user.last_name}`
            },
            {
              label: "Account",
              value: "set",
              render: () => (
                <Badge size="sm" colorPalette={isFlagOn(user.active) ? 'green' : 'red'} variant="subtle">
                  {isFlagOn(user.active) ? 'Active' : 'Inactive'}
                </Badge>
              )
            },
            {
              label: "Profile ID",
              value: user.profile?.id
            },
            {
              label: "Profile Slug",
              value: user.slug,
              render: (value) => value ? (
                <Link 
                  as="button"
                  onClick={() => window.open(
                    getUserProfileUrl(value),
                    'userProfile',
                    'width=800,height=800,scrollbars=yes,resizable=yes'
                  )}
                  fontFamily="mono" 
                  fontSize="sm"
                  color="blue.600"
                  _dark={{ color: "blue.400" }}
                  _hover={{ textDecoration: "underline", cursor: "pointer" }}
                >
                  {value}
                </Link>
              ) : <Text>N/A</Text>
            },
            {
              label: "Title",
              value: user.title || user.profile?.title?.title
            },
            {
              label: "Occupation",
              value: user.profile?.occupation
            },
            {
              label: "Profile Status",
              value: user.profile?.status,
              render: () => (
                <Badge
                  size="sm"
                  colorPalette={getUserStatusColor(user.profile?.status)}
                  variant="subtle"
                >
                  {user.profile?.status}
                </Badge>
              )
            },
            {
              label: "Created",
              value: user.created_at ? "set" : null,
              render: () => <RelativeTime date={user.created_at} />
            },
            {
              label: "Updated", 
              value: user.updated_at ? "set" : null,
              render: () => <RelativeTime date={user.updated_at} fallbackDate={user.created_at} />
            }
          ]}
        />

        {/* Roles & Organization Section */}
        <DetailsSection
          title="Roles & Organization"
          icon={Building2}
          items={[
            {
              label: "Roles", 
              value: user.roles?.length ? "assigned" : null,
              render: () => (
                <VStack align="end" gap={1}>
                  {user.roles?.length > 0 ? (
                    user.roles.map((role, index) => (
                      <Badge 
                        key={index}
                        size="sm"
                        colorPalette="blue"
                        variant="outline"
                      >
                        {getRoleLabelByValue(role)}
                      </Badge>
                    ))
                  ) : (
                    <Text fontSize="sm" color="gray.500">No roles assigned</Text>
                  )}
                </VStack>
              )
            },
            {
              label: "Organization",
              value: user.organization?.name || 'N/A'
            }
          ]}
        />

        <ProfileAccountStatusSection user={user} />

        {user.profile?.id && user.roles?.includes('doctor') ? (
          <ClarityChatbotOptInSection profileId={user.profile.id} isOpen={isOpen} />
        ) : null}
      </VStack>
    );
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      allowAgentChatInteraction
    >
      <DrawerHeader>
        <DrawerTitle color="brand.700" _dark={{ color: "brand.300" }}>
          User Details
        </DrawerTitle>
        <DrawerCloseTrigger />
      </DrawerHeader>
      
      <DrawerBody>
        {renderContent()}
      </DrawerBody>
      
      <DrawerFooter>
        <HStack justify="space-between" w="full">
          <HStack>
            {user && onImpersonateUser && (
              <Button
                colorPalette="orange"
                variant="outline"
                onClick={() => onImpersonateUser(user.id)}
                disabled={isImpersonatingUser}
                loading={isImpersonatingUser}
              >
                <Icon as={UserCheck} boxSize={4} mr={2} />
                Impersonate
              </Button>
            )}
            {entityId && navigateToModal && (
              <Button 
                variant="outline" 
                onClick={() => navigateToModal(entityId, 'edit')}
              >
                <Icon as={Edit} boxSize={4} mr={2} />
                Edit
              </Button>
            )}
          </HStack>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </HStack>
      </DrawerFooter>
    </Drawer>
  );
};

export default withHashDrawer(UserDetailsModal, {
  action: 'view'
});
